<template>
    <hover-card
        id="segment-hover-card"
        class="segment-hover-card"
        data-cy="segment-hover-card"
        for-multiselect
        :title="title"
        :title-lines-to-show="1"
        :metrics="metrics"
        :metrics-subtitle="
            $t('segmentHoverCard.metricsSubtitle.Eligible visitors are based on usage in the last year.')
        "
        :height="height">
        <template #actions>
            <div
                v-if="!isPendoDefaultSegment && !isCustomSegment"
                class="segment-hover-card__actions">
                <div
                    v-pendo-tooltip="{ content: verificationTooltip, multiline: true, arrow: true }"
                    class="segment-hover-card__label">
                    <pendo-icon
                        :type="verificationConfig.icon.type"
                        :fill="verificationConfig.icon.fill"
                        :stroke="verificationConfig.icon.stroke"
                        :size="16" />
                    {{ verificationConfig.label }}
                </div>
                <div class="segment-hover-card__actions-buttons-wrapper">
                    <star
                        :id="segmentId"
                        v-pendo-tooltip="{ content: $t('segmentHoverCard.star.Favorite'), position: 'top' }"
                        class="segment-details-drawer--star-icon pendo-button"
                        type="Segment" />
                    <pendo-router-link
                        v-if="!isCustomSegment"
                        v-pendo-tooltip="{ content: $t('segmentHoverCard.details.View details'), position: 'top' }"
                        :to="{ name: 'Segments', query: { segment: segment.id } }"
                        target="_blank"
                        :aria-label="$t('segmentHoverCard.details.Open in new tab')">
                        <pendo-button
                            class="segment-hover-card__actions-button"
                            type="tertiary"
                            size="mini"
                            tabindex="-1"
                            aria-hidden="true">
                            <template #default>
                                <pendo-icon
                                    type="external-link"
                                    size="16"
                                    stroke-width="2" />
                            </template>
                        </pendo-button>
                    </pendo-router-link>
                    <pendo-multiselect
                        v-if="showActionsMenu"
                        ref="actionsMultiselect"
                        class="segment-hover-card__actions-multiselect"
                        :options="actionOptions"
                        :append-to-body="false"
                        :popper-options="popperOptions"
                        @select="onSelectAction">
                        <template #option="{ option }">
                            <pendo-icon-option
                                v-pendo-tooltip="{
                                    content: option.id === 'edit' ? editButtonTooltip : copyButtonTooltip,
                                    multiline: true,
                                    arrow: true,
                                }"
                                :class="`segment-hover-card__${option.action}-button`"
                                :option="option" />
                        </template>
                        <template #trigger="{ comboboxAttrs, handleKeydown, toggleMenu }">
                            <pendo-button
                                v-bind="comboboxAttrs"
                                class="segment-hover-card__actions-button"
                                data-cy="segment-hover-card-more-actions-button"
                                size="mini"
                                type="tertiary"
                                :aria-label="$t('segmentHoverCard.More options')"
                                @click="toggleMenu"
                                @keydown="handleKeydown">
                                <template #default>
                                    <pendo-icon
                                        type="more-horizontal"
                                        size="16"
                                        stroke-width="2" />
                                </template>
                            </pendo-button>
                        </template>
                    </pendo-multiselect>
                </div>
            </div>
            <div
                v-else
                class="segment-hover-card__actions">
                <div class="segment-hover-card__label">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="13"
                        viewBox="0 0 12 13"
                        fill="none">
                        <g clip-path="url(#clip0_2218_15233)">
                            <path
                                d="M5.99963 0.5L0 6.49963H5.99963V12.4993L12 6.49963V0.5H5.99963Z"
                                fill="var(--palette-pink-120)" />
                        </g>
                        <defs>
                            <clipPath id="clip0_2218_15233">
                                <rect
                                    width="12"
                                    height="12"
                                    fill="var(--palette-gray-0)"
                                    transform="translate(0 0.5)" />
                            </clipPath>
                        </defs>
                    </svg>
                    {{ $t('segmentHoverCard.actions.Created by {productName}', { productName: isAdoptEndUser ? trainingAttributes?.productName || 'OEM' : 'Pendo' }) }}
                </div>
                <div v-if="isCustomSegment">
                    <pendo-button
                        v-pendo-tooltip="{ content: $t('segmentHoverCard.customSegment.Edit'), position: 'top' }"
                        class="segment-hover-card__actions-button"
                        size="mini"
                        type="tertiary"
                        @click="$emit('edit', segment)">
                        <template #default>
                            <pendo-icon
                                type="edit"
                                size="16"
                                stroke-width="2" />
                        </template>
                    </pendo-button>
                </div>
            </div>
        </template>
        <template #subtitle>
            <div
                class="segment-hover-card__description"
                :class="{ 'no-truncation': isPendoDefaultSegment }">
                {{ segment.description }}
            </div>
        </template>
        <template
            #body
            v-if="!isPendoDefaultSegment">
            <segment-rules-list
                ref="rulesList"
                class="segment-hover-card__rules"
                :class="{
                    'off-top': hasScrolled,
                    'off-bottom': canScroll,
                }"
                :show-title="false"
                :segment="segment"
                :rules="rules"
                :show-anonymous-visitor-data="false"
                :show-identified-label="false" />
        </template>
    </hover-card>
</template>

<script>
import { PendoButton, PendoIcon, PendoIconOption, PendoMultiselect, PendoTooltip } from '@pendo/components';
import { mapGetters, mapState, mapActions } from 'vuex';
import HoverCard from '@/common/hover-card/hover-card.vue';
import { getStateOptions, tooltipContent } from '@/common/verification-status/verification-status.utils';
import Star from '@/common/star/star';
import { $canSub } from '@pendo/permissions';
import { DEFAULT_SEGMENTS } from '@/utils/segments';
import { overMauLimitMessage } from '@/free/free.constants';
import SegmentRulesList from '@/guides/p2-guide-details/guide-settings/segment-card/segment-rules-list.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { LOADING_STATES } from '@/constants';
import visitorList from '@/vuex/modules/segments/segment-eligible-visitor-list.aggregation';
import { timer } from 'rxjs';
import { switchMap, catchError, startWith, distinctUntilChanged, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import {
    withComponent,
    sendAggregation,
    connectToState,
    createRejectedStatus,
    loadingStatus,
} from '@pendo/agg-connect';
import debounce from 'lodash/debounce';
import { ref } from 'vue';
import { useScrollShadows } from '@/common/use/use-scroll-shadows/use-scroll-shadows';

export default {
    name: 'SegmentHoverCard',
    components: {
        HoverCard,
        PendoButton,
        PendoIcon,
        PendoIconOption,
        PendoMultiselect,
        Star,
        SegmentRulesList,
        PendoRouterLink,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        segmentId: {
            type: String,
            required: true,
        },
        segmentDependents: {
            type: Object,
            default: () => ({}),
        },
        height: {
            type: String,
            default: '320px',
        },
        showBuilderActions: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['edit', 'copy'],
    setup () {
        const rulesList = ref(null);
        const { canScroll, hasScrolled } = useScrollShadows(rulesList);

        return {
            rulesList,
            canScroll,
            hasScrolled,
        };
    },
    data () {
        return {
            visitorsValue: 0,
            isLoadingVisitorMetric: true,
            errorLoadingVisitorMetric: false,
            isLoadDependentsQueued: false,
            popperOptions: {
                placement: 'bottom-end',
            },
            aggRunTime: -1,
            vuexRunTime: -1,
        };
    },
    computed: {
        ...mapState({
            currentUser: (state) => state.auth.user,
            customSegment: (state) => state.segments.customSegment,
        }),
        ...mapGetters({
            segmentById: 'segments/segmentById',
            isFreeAndOverMauLimit: 'subscription/isFreeAndOverMauLimit',
            isAdoptEndUser: 'subscription/isAdoptEndUser',
            trainingAttributes: 'subscription/trainingAttributes',
        }),
        isCustomSegment () {
            return this.segmentId === 'custom';
        },
        segment () {
            if (this.isCustomSegment) {
                return this.customSegment;
            }

            if (this.isPendoDefaultSegment) {
                const defaultSegment = {
                    ...this.segmentById(this.segmentId),
                    pipeline: undefined,
                    enabled: undefined,
                };

                return defaultSegment;
            }

            return (
                this.segmentById(this.segmentId) || {
                    id: this.segmentId,
                    name: 'Unknown Segment',
                }
            );
        },
        rules () {
            if (!this.segment?.definition?.filters.length) {
                return 'everyone';
            }

            return this.segment?.definition?.filters;
        },
        isPendoDefaultSegment () {
            const defaultIds = Object.keys(DEFAULT_SEGMENTS);

            return defaultIds.includes(this.segmentId);
        },
        actionOptions () {
            const options = [];
            if (this.showEditButton) {
                options.push({
                    id: 'edit',
                    label: this.$t('segmentHoverCard.actionOptions.Edit'),
                    icon: { type: 'edit' },
                    action: 'edit',
                    disabled: this.isEditButtonDisabled,
                });
            }
            if (this.showCopyButton) {
                options.push({
                    id: 'duplicate',
                    label: this.$t('segmentHoverCard.actionOptions.Duplicate'),
                    icon: { type: 'copy' },
                    action: 'copy',
                    disabled: this.isCopyButtonDisabled,
                });
            }

            return options;
        },
        title () {
            const title = `${this.segment.name} ${this.segment.aside || ''}`.trim();

            return this.segment?.id?.startsWith('_PENDO_DEFAULT_')
                ? this.$t('segmentHoverCard.title', { title })
                : title;
        },
        verificationConfig () {
            const stateOptions = getStateOptions();

            switch (this.segment.verification?.state) {
                case 'Verified':
                    return stateOptions.verified;
                case 'Unverified':
                    return stateOptions.unverified;
                case 'Requested':
                    return stateOptions.requested;
                case 'Rejected':
                    return stateOptions.rejected;
                default:
                    return stateOptions.unverified;
            }
        },
        verificationTooltip () {
            return tooltipContent(this.segment.verification || {});
        },
        canEdit () {
            return $canSub()
                .entity('segment')
                .action('edit')
                .source('*')
                .target('shared');
        },
        showEditButton () {
            return (
                !this.segment.isFocusGroup && (this.currentUser.id === this.segment.createdByUser?.id || this.canEdit)
            );
        },
        isEditButtonDisabled () {
            return this.segment.createdByApi;
        },
        editButtonTooltip () {
            if (this.isEditButtonDisabled) {
                return this.$t('segmentHoverCard.editButtonTooltip.You must use the API to edit API-created segments');
            }

            return '';
        },
        showCopyButton () {
            return !this.segment.isFocusGroup;
        },
        isCopyButtonDisabled () {
            return this.isFreeAndOverMauLimit || this.segment.createdByApi;
        },
        copyButtonTooltip () {
            if (this.isFreeAndOverMauLimit) {
                return overMauLimitMessage;
            }

            if (this.segment.createdByApi) {
                return this.$t(
                    "segmentHoverCard.copyButtonTooltip.createdByApi.You can't duplicate API-created segments",
                );
            }

            return '';
        },
        showActionsMenu () {
            return this.showBuilderActions && (this.showEditButton || this.showCopyButton);
        },
        isLoadingDependents () {
            return this.segmentDependents?.fetching || this.isLoadDependentsQueued;
        },
        eligibleVisitorsMetric () {
            return {
                label: this.$t(
                    'segmentHoverCard.eligibleVisitorsMetric.Eligible {eligibleVisitors, plural, one {visitor} other {visitors}}',
                    {
                        eligibleVisitors: this.visitorsValue,
                    },
                ),
                icon: 'user',
                value: this.visitorsValue,
                loading: this.isLoadingVisitorMetric,
                error: this.errorLoadingVisitorMetric,
            };
        },
        reportsMetric () {
            if (this.isPendoDefaultSegment || this.isCustomSegment) return null;

            return {
                label: this.$t('segmentHoverCard.reportsMetric.label', {
                    reportCount: this.reportsValue,
                }),
                icon: 'bar-chart-big',
                value: this.reportsValue,
                loading: this.isLoadingDependents,
                error: !this.isLoadingDependents && this.reportsValue === undefined,
            };
        },
        guidesMetric () {
            if (this.isPendoDefaultSegment || this.isCustomSegment) return null;

            return {
                label: this.$t('segmentHoverCard.guidesMetric.label', { guideCount: this.guidesValue }),
                icon: 'guide',
                value: this.guidesValue,
                loading: this.isLoadingDependents,
                error: !this.isLoadingDependents && this.guidesValue === undefined,
            };
        },
        metrics () {
            return [this.eligibleVisitorsMetric, this.reportsMetric, this.guidesMetric].filter(Boolean);
        },
        reportsValue () {
            if (this.isPendoDefaultSegment || this.isCustomSegment) return 0;

            return this.segmentDependents?.dependentReports?.length;
        },
        guidesValue () {
            if (this.isPendoDefaultSegment || this.isCustomSegment) return 0;

            return this.segmentDependents?.dependentGuides?.length;
        },
    },
    watch: {
        segmentId: {
            handler () {
                this.aggRunTime = -1;
                this.vuexRunTime = -1;
                if (!this.isPendoDefaultSegment) {
                    this.isLoadDependentsQueued = true;
                    this.loadDependentsDebounced();
                }
                this.$refs.actionsMultiselect?.$refs.core.deactivate();
            },
            immediate: true,
        },
    },
    async created () {
        this.STREAM_ID = uuid();

        this.$watch(
            () => ({
                isPendoDefaultSegment: this.isPendoDefaultSegment,
                vuexRunTime: this.vuexRunTime,
                aggRunTime: this.aggRunTime,
            }),
            ({ isPendoDefaultSegment, vuexRunTime, aggRunTime }) => {
                if ((isPendoDefaultSegment || vuexRunTime >= 0) && aggRunTime >= 0) {
                    this.sendTrackEvent();
                }
            },
            { deep: true },
        );

        const metrics$ = this.createMetricsObservable();
        this.metricsSubscription = metrics$.subscribe(this.handleMetricsResponse);
    },
    unmounted () {
        this.metricsSubscription?.unsubscribe();
    },
    methods: {
        ...mapActions('segments', ['loadDependents']),
        async loadSegmentsDependents () {
            const vuexStartTime = Date.now();
            await this.loadDependents({ segments: [this.segment], reFetch: false });
            this.vuexRunTime = Date.now() - vuexStartTime;
            this.isLoadDependentsQueued = false;
        },
        loadDependentsDebounced () {
            return debounce(this.loadSegmentsDependents, 500)();
        },
        createMetricsObservable () {
            const state$ = connectToState(
                withComponent({
                    segment: 'segment',
                    livePreview: 'isCustomSegment',
                }),
            )({ component: this });

            return state$.pipe(
                switchMap(({ segment, livePreview }) => {
                    const agg = visitorList;

                    return timer(500).pipe(
                        tap(() => {
                            this.aggStartTime = Date.now();
                        }),
                        switchMap(() => sendAggregation(agg, this.STREAM_ID, { segment, livePreview })),
                        startWith(loadingStatus),
                    );
                }),
                catchError((error, caught) => {
                    return caught.pipe(startWith(createRejectedStatus(error)));
                }),
                distinctUntilChanged(),
            );
        },
        handleMetricsResponse ({ status, value }) {
            const { LOADING, RESOLVED, REJECTED } = LOADING_STATES;
            switch (status) {
                case LOADING:
                    this.isLoadingVisitorMetric = true;
                    this.errorLoadingVisitorMetric = false;
                    this.visitorsValue = 0;
                    break;
                case RESOLVED:
                    this.isLoadingVisitorMetric = false;
                    this.errorLoadingVisitorMetric = false;
                    this.visitorsValue = value[0].eligibleVisitorCount || 0;
                    this.aggRunTime = Date.now() - this.aggStartTime;
                    break;
                case REJECTED:
                    this.isLoadingVisitorMetric = false;
                    this.errorLoadingVisitorMetric = true;
            }
        },
        sendTrackEvent () {
            window.pendo.track('Hover Card', {
                pageLocation: this.$route?.name || 'Unknown',
                eventType: 'segment',
                aggTime: this.aggRunTime,
                vuexTime: this.vuexRunTime >= 0 ? this.vuexRunTime : undefined,
            });
        },
        onSelectAction (option) {
            this.$emit(option.action, this.segment);
        },
    },
};
</script>

<style lang="less" scoped>
.segment-hover-card {
    :deep(.hover-card__body) {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        gap: 8px;
    }

    &__actions {
        display: flex;
        justify-content: space-between;
        align-items: center;

        &-buttons-wrapper {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        :deep(.pendo-button--mini) {
            height: 16px;
            line-height: 16px;
        }

        &-button {
            height: 16px;
            color: var(--palette-gray-70);

            &:hover {
                color: var(--palette-gray-100);
            }
        }
    }

    &__actions-multiselect {
        .pendo-picker-popper__content {
            border: none !important;
        }
    }

    &__label {
        display: flex;
        gap: 4px;
        font-size: 13px;
        align-items: center;
        height: 19px;
    }

    &__description {
        font-size: 13px;
        font-weight: 400;
        color: var(--palette-gray-70);
        max-height: 43px;
        line-height: 150%;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-wrap: break-word;
    }

    &__rules {
        flex-grow: 1;
        font-size: 12px;
        overflow-y: auto;
        position: relative;
    }

    &__rules::before,
    &__rules::after {
        content: '';
        display: block;
        position: sticky;
        left: 0;
        width: 100%;
        height: 45px;
        margin-top: -45px;
        pointer-events: none;
        transition: box-shadow 0.2s ease-out;
    }

    &__rules::before {
        top: 0;
    }

    &__rules.off-top::before {
        box-shadow: inset 0 30px 15px -15px var(--palette-gray-0);
    }

    &__rules::after {
        bottom: 0;
    }

    &__rules.off-bottom::after {
        box-shadow: inset 0 -30px 15px -15px var(--palette-gray-0);
    }

    .no-truncation {
        display: block;
        max-height: 140px;
    }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.segment-hover-card__actions-multiselect {
    .pendo-picker-popper__content {
        border: none !important;
    }
}
</style>
